import React, { useCallback, useEffect, useState } from "react";
import {
  Drawer,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { CheckboxGroup } from "@/components/ui/checkbox-group";
import { Close } from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import {
  DragDropContext,
  Droppable,
  DropResult,
  DragUpdate
} from "@hello-pangea/dnd";
import DraggableColumnItem from "@/components/layouts/ColumnsManagement/DraggableColumnItem";
import { ColumnData } from "@/entities/dataTables/types";
import { ViewDataConfiguration, ViewType } from "@/entities/views/types";
import {
  updateTempViewColumnsState,
  createColumnLabelResolver,
  initializeColumnsWithLabels,
  getViewColumns,
  getIsCheckboxDisabled,
  handleColumnChange
} from "@/entities/dataTables/helpers";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import SearchInput from "@/components/ui/search-input";
import { IconButton } from "@/components/ui/icon-button";
import { APIFieldItem } from "@/pages/settings/types";
import { debounce } from "lodash";

interface Props {
  isOpen: boolean;
  selectedView: ViewDataConfiguration;
  defaultColumnsState: ColumnData[];
  onClose: () => void;
  onApply: (updatedColumns: ColumnData[]) => void;
  tableId: string;
  className?: string;
  customFields?: APIFieldItem[];
}

const DealsColumnsManagement: React.FC<Props> = ({
  isOpen,
  selectedView,
  defaultColumnsState,
  onClose,
  onApply,
  tableId,
  className,
  customFields
}) => {
  const { t } = useTranslation();
  const { label: arrMrrLabel, fieldName: arrMrrField } = useArrMrr();

  const [columnStates, setColumnStates] =
    useState<ColumnData[]>(defaultColumnsState);
  const [dragState, setDragState] = useState<{
    isDragging: boolean;
    destinationIndex: number | null;
  }>({
    isDragging: false,
    destinationIndex: null
  });
  const [searchValue, setSearchValue] = useState("");

  const labelResolver = useCallback(
    createColumnLabelResolver(
      tableId,
      t,
      arrMrrLabel,
      arrMrrField,
      customFields,
      new Map(defaultColumnsState.map((column) => [column.id, column.label]))
    ),
    [tableId, t, arrMrrLabel, arrMrrField, customFields, defaultColumnsState]
  );

  useEffect(() => {
    if (isOpen) {
      const mergedColumns = getViewColumns(selectedView, defaultColumnsState);

      const initializedColumns = initializeColumnsWithLabels(
        mergedColumns,
        defaultColumnsState,
        labelResolver,
        true // Use custom fields merging for deals
      );

      setColumnStates(initializedColumns);
    }
  }, [isOpen, selectedView, defaultColumnsState, labelResolver]);

  const handleApply = () => {
    const updatedColumns = updateTempViewColumnsState(
      columnStates,
      selectedView
    );
    if (updatedColumns) {
      onApply(updatedColumns);
    }
    handleClose();
  };

  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      if (searchTerm.trim() === "") {
        setColumnStates(defaultColumnsState);
        return;
      }

      const filtered = defaultColumnsState.filter((column) =>
        column.label.toLowerCase().includes(searchTerm.toLowerCase())
      );

      setColumnStates(filtered);
    }, 300),
    [defaultColumnsState]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleColumnChangeLocal = (column: ColumnData, checked: boolean) => {
    handleColumnChange(column, checked, columnStates.length, setColumnStates);
  };

  const handleDragUpdate = (update: DragUpdate) => {
    if (!update.destination) {
      setDragState({ isDragging: true, destinationIndex: null });
      return;
    }
    setDragState({
      isDragging: true,
      destinationIndex: update.destination.index
    });
  };

  const handleDragEnd = (result: DropResult) => {
    setDragState({ isDragging: false, destinationIndex: null });
    const { destination, source } = result;

    if (!destination) return;
    if (destination.index === source.index) return;

    setColumnStates((currentColumns) => {
      const newColumns = [...currentColumns];
      const [removed] = newColumns.splice(source.index, 1);
      newColumns.splice(destination.index, 0, removed);
      return newColumns.map((column, index) => ({
        ...column,
        order: index
      }));
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleClose = () => {
    setSearchValue("");
    onClose();
  };

  // Only render for deals views
  if (
    selectedView.type !== ViewType.DEALS_KANBAN &&
    selectedView.type !== ViewType.DEALS_TABLE
  ) {
    return null;
  }

  return (
    <Drawer
      open={isOpen}
      direction="right"
      fixed
      modal={false}
      disablePreventScroll={true}
      noBodyStyles={true}
      dismissible={false}
    >
      <AppDrawerContent
        className={cn(
          "max-w-[25vw] z-50 border-[0.5px] border-border-border2 rounded-l-none",
          className
        )}
      >
        <DrawerHeader divider={false}>
          <DrawerTitle>{t("datatoolbar.columnsManagement.title")}</DrawerTitle>
          <IconButton variant="iconNoBg" size="sm" onClick={handleClose}>
            <Close />
          </IconButton>
        </DrawerHeader>
        <DrawerDescription>
          {t("datatoolbar.columnsManagement.title")}
        </DrawerDescription>
        <div className="w-full flex max-h-[70vh] overflow-y-auto flex-col gap-2 h-full py-2">
          <SearchInput
            placeholder={t("search")}
            value={searchValue}
            onChange={handleSearchChange}
          />
          <DragDropContext
            onDragEnd={handleDragEnd}
            onDragUpdate={handleDragUpdate}
          >
            <Droppable
              droppableId="columns-list"
              isDropDisabled={columnStates.length <= 1}
            >
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  <CheckboxGroup fullWidth color="var(--cta)">
                    {columnStates.map((column, index) => (
                      <React.Fragment key={column.id}>
                        <DraggableColumnItem
                          key={column.id}
                          column={column}
                          index={index}
                          onColumnChange={handleColumnChangeLocal}
                          isDragDisabled={columnStates.length <= 1}
                          isCheckboxDisabled={getIsCheckboxDisabled(
                            column,
                            columnStates.length
                          )}
                        />
                        {dragState.isDragging &&
                          dragState.destinationIndex === index && (
                            <div className="h-[1px] bg-cta w-full transition-opacity duration-200" />
                          )}
                      </React.Fragment>
                    ))}
                  </CheckboxGroup>
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
        <DrawerFooter padded>
          <div className="flex gap-2 w-full justify-end">
            <Button variant="secondary" onClick={handleClose}>
              {t("cancel")}
            </Button>
            <Button onClick={handleApply}>{t("apply")}</Button>
          </div>
        </DrawerFooter>
      </AppDrawerContent>
    </Drawer>
  );
};

export default DealsColumnsManagement;
